import React, { useId, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import './IconButton.module.scss'

const VARIANT_CLASS = {
  neutral: 'Uic-IconButton-neutral',
  primary: 'Uic-IconButton-primary',
  danger: 'Uic-IconButton-danger',
  ghost: 'Uic-IconButton-ghost',
}
const TOOLTIP_DELAY = { show: 250, hide: 400 }
const TOOLTIP_TRIGGER = ['hover', 'focus']
const BLANK_TARGET_REL = 'noopener noreferrer'

const propTypes = {
  /** Font Awesome icon class, e.g. "fas fa-wrench" */
  icon: PropTypes.string.isRequired,
  /** Id set on the rendered `<button>` / `<a>` element. */
  id: PropTypes.string,
  /** Accessible label — used as the aria-label, and as the tooltip when `tooltip` is omitted */
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  /** When set, renders an `<a>` so right-click / open-in-new-tab work like any other link. */
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  /** Visual emphasis. Reserve `primary` for a single recommended action per group; `danger` for
   *  destructive; `ghost` drops the box for a bare inline glyph (copy, open-in-new-tab). */
  variant: PropTypes.oneOf(['neutral', 'primary', 'danger', 'ghost']),
  /** Tooltip content; falls back to `label` when omitted. */
  tooltip: PropTypes.node,
  disabled: PropTypes.bool,
  /** For a button that opens a menu or panel: whether it is open. Set by react-bootstrap's `Dropdown.Toggle`. */
  'aria-expanded': PropTypes.bool,
}

/**
 * Small square icon-only action (22x22). The icon-only member of the button family;
 * pair several inside a row's action group. Always requires a `label` for accessibility.
 * Pass `href` to render a link with the same chrome as the button.
 */
function IconButton({
  icon,
  id = undefined,
  label,
  onClick,
  href,
  target,
  rel,
  variant = 'neutral',
  tooltip = null,
  disabled = false,
  'aria-expanded': ariaExpanded = undefined,
}) {
  const tooltipId = useId()
  const [show, setShow] = useState(false)
  const overlayContent = tooltip ?? label
  const className = clsx('Uic-IconButton-base', VARIANT_CLASS[variant] ?? VARIANT_CLASS.neutral)

  const handleClick = (event) => {
    setShow(false)
    event.currentTarget.blur()
    onClick?.(event)
  }

  const iconEl = <i className={icon} aria-hidden="true" />
  const isLink = Boolean(href) && !disabled

  let control
  if (isLink) {
    const linkRel = rel ?? (target === '_blank' ? BLANK_TARGET_REL : undefined)
    control = (
      <a
        id={id}
        href={href}
        target={target}
        rel={linkRel}
        onClick={handleClick}
        aria-label={label}
        className={className}
      >
        {iconEl}
      </a>
    )
  } else {
    control = (
      <button
        id={id}
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={label}
        aria-expanded={ariaExpanded}
        className={className}
      >
        {iconEl}
      </button>
    )
  }

  if (!overlayContent) {
    return control
  }

  return (
    <OverlayTrigger
      placement="top"
      trigger={TOOLTIP_TRIGGER}
      delay={TOOLTIP_DELAY}
      show={show}
      onToggle={setShow}
      overlay={<Tooltip id={tooltipId}>{overlayContent}</Tooltip>}
    >
      {disabled ? <span className="d-inline-flex">{control}</span> : control}
    </OverlayTrigger>
  )
}

IconButton.propTypes = propTypes

export default React.memo(IconButton)
